import { AxiosInstance } from 'axios';
import axiosClient from './axiosClientConfig';
import { HttpResponse } from 'interfaces/httpClient';

import {
	CaseStatusI,
	CaseI,
	RequestGetAllI,
	CaseDetailI,
	CaseQuestionI,
	CaseQuestionEvaluateI,
	CaseTypesResponse,
	CaseEntitiesResponse,
	UploadCaseDocumentResponse,
	SavedOptConfigResponse,
	SignatureData,
	OptAcceptPayload,
	OptAccept,
	CaseQuestionsPreEvaluate,
} from '@/interfaces/cases';
import { State, uploadDocumentType } from '@/interfaces';

/**
 * This class should hold all the routes to the endpoints that are
 * related to the cases handling inside the app.
 */

class CasesService {
	constructor(private client: AxiosInstance) {}

	async getAll({
		page = 1,
		limit = 9,
		search,
		categoryId,
		statusId,
		eligibleStateId,
	}: RequestGetAllI): Promise<HttpResponse<CaseI>> {
		const searchParam = search ? `&search=${search}` : '';
		const categoryParam = categoryId ? `&categoryId=${categoryId}` : '';
		const statusParam = statusId ? `&statusId=${statusId}` : '';
		const stateIdParam = eligibleStateId
			? `&eligibleStateId=${eligibleStateId}`
			: '';

		const url = `/cases?page=${page}&limit=${limit}${searchParam}${categoryParam}${statusParam}${stateIdParam}`;
		return this.client.get(url, {});
	}

	async getById(id: string): Promise<HttpResponse<CaseDetailI>> {
		return this.client.get(`/cases/${id}`);
	}

	async getCasesStatus(): Promise<HttpResponse<CaseStatusI[]>> {
		return this.client.get('/cases/case_status');
	}

	async getByQuantity(count = 20): Promise<HttpResponse<CaseI>> {
		return this.client.get(`/cases?page=${1}&limit=${count}&statusId=1`);
	}

	async getQuestions(id: string): Promise<HttpResponse<CaseQuestionI>> {
		return this.client.get(`/claims/${id}/questions`);
	}

	async evaluateClaim(
		id: string,
		body: CaseQuestionEvaluateI
	): Promise<HttpResponse<boolean>> {
		return this.client.post(`/claims/${id}/evaluate`, body);
	}

	async preEvaluateClaim(id: string, body: CaseQuestionsPreEvaluate) {
		return this.client.post(`/claims/${id}/evaluate-ai`, body);
	}

	async getStates(): Promise<HttpResponse<State[]>> {
		return this.client.get('/cases/states');
	}

	async getTypes(): Promise<HttpResponse<CaseTypesResponse>> {
		return this.client.get('/cases/types');
	}

	async getEntities(): Promise<HttpResponse<CaseEntitiesResponse>> {
		return this.client.get('/cases/entities');
	}

	async delete(caseId: string): Promise<HttpResponse<{ message: string }>> {
		return this.client.delete(`/cases/${caseId}`);
	}

	// ------------------- Opt Out ---------------------//
	async getAcceptedOptSignatures(
		caseId: string,
		page: number,
		limit: number
	): Promise<HttpResponse<any>> {
		return this.client.get(
			`cases-active/opt-out-request-acepted-list/${caseId}?page=${page}&limit=${limit}`
		);
	}
	async getSavedOptConfig(
		caseId: string
	): Promise<HttpResponse<SavedOptConfigResponse>> {
		return this.client.get(`cases-active/get-otp-step/${caseId}`);
	}
	async getAllOptSignatures(
		caseId: string
	): Promise<HttpResponse<SignatureData[]>> {
		return this.client.get(
			`cases-active/all-opt-out-request-acepted-list/${caseId}`
		);
	}
	async savedOptConfig(
		caseId: string,
		required: boolean
	): Promise<HttpResponse<any>> {
		return this.client.put(`cases-active/save-otp-step/${caseId}/${required}`);
	}
	async completeOptConfig(
		caseId: string,
		required: boolean
	): Promise<HttpResponse<any>> {
		return this.client.post(
			`cases-active/complete-otp-step/${caseId}/${required}`
		);
	}
	async acceptOptOut(
		applicationId: number,
		data: OptAcceptPayload
	): Promise<HttpResponse<SavedOptConfigResponse>> {
		return this.client.post(
			`/cases-active/opt-out-request/${applicationId}`,
			data
		);
	}

	async getOptOutAccepted(
		caseId: string,
		applicationId: number
	): Promise<HttpResponse<OptAccept>> {
		return this.client.get(
			`/cases-active/opt-out-request-acepted/${caseId}/${applicationId}`
		);
	}

	async uploadCaseDocument(
		caseId: string,
		documentType: uploadDocumentType,
		prevDocumentId?: number,
		file?: File
	): Promise<HttpResponse<UploadCaseDocumentResponse>> {
		const formData = new FormData();
		formData.append('caseId', caseId);
		formData.append('documentType', documentType);
		prevDocumentId &&
			formData.append('documentId', prevDocumentId.toLocaleString());
		file && formData.append('file', file);

		return this.client.post('/cases-active/active-case/upload', formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
	}

	async getStatusApproveClient(
		id: number | undefined
	): Promise<HttpResponse<any>> {
		return this.client.get(`/action-required/approve-reject/${id}`);
	}
}

export const casesService = new CasesService(axiosClient);
